import React, { useState, useEffect } from 'react';


const SpeedTest = () => {
  // State for test status and data
  const [testStatus, setTestStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [currentTest, setCurrentTest] = useState('');
  const [scanAngle, setScanAngle] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Initialize all numeric values with 0 to ensure they're numbers
  const [speedData, setSpeedData] = useState({
    download_speed_mbps: 0,
    upload_speed_mbps: 0,
    ping_ms: 0,
    jitter_ms: 0
  });
  
  const [ipInfo, setIpInfo] = useState({
    public_ip: '0.0.0.0',
    local_ip: '0.0.0.0',
    isp: 'Unknown',
    asn: 'AS0000',
    dns_servers: []
  });
  
  const [packetLoss, setPacketLoss] = useState(0);
  const [natType, setNatType] = useState('Unknown');
  const [bufferbloat, setBufferbloat] = useState({
    idle_latency_ms: 0,
    download_latency_ms: 0,
    upload_latency_ms: 0,
    bufferbloat_ms: 0
  });
  
  const [traceroute, setTraceroute] = useState({
    hops: []
  });
  
   
  const [testHistory, setTestHistory] = useState([]);
  const [serverList, setServerList] = useState([
    { id: 1, name: 'New York', distance: '23 mi', latency: '12 ms' },
    { id: 2, name: 'London', distance: '3,471 mi', latency: '78 ms' },
    { id: 3, name: 'Tokyo', distance: '6,758 mi', latency: '126 ms' },
    { id: 4, name: 'Sydney', distance: '9,934 mi', latency: '189 ms' }
  ]);
  const [selectedServer, setSelectedServer] = useState(1);
  
  // Scan animation effect
  useEffect(() => {
    let interval;
    if (testStatus === 'running') {
      interval = setInterval(() => {
        setScanAngle(prev => (prev + 2) % 360);
      }, 20);
    }
    return () => clearInterval(interval);
  }, [testStatus]);
  
  // Function to calculate connection quality
  const calculateConnectionQuality = (speedData, packetLoss) => {
    // 1. Validate inputs first
    const safeNumber = (value, max) => 
      Math.min(max, Math.max(0, Number(value) || 0));
  
    // 2. Apply realistic maximums
    const download = safeNumber(speedData.download_speed_mbps, 300); // Max 300 Mbps
    const upload = safeNumber(speedData.upload_speed_mbps, 100);     // Max 100 Mbps
    const ping = safeNumber(speedData.ping_ms, 500);                // Max 500 ms
    const jitter = safeNumber(speedData.jitter_ms, 50);             // Max 50 ms
    const loss = safeNumber(packetLoss, 100);                       // Max 100%
  
    // 3. Calculate with ceilings
    const downloadScore = Math.min(100, download / 2);
    const uploadScore = Math.min(100, upload * 2);
    const pingScore = Math.max(0, 100 - ping * 0.4); // Adjusted for realistic ping
    const jitterScore = Math.max(0, 100 - jitter * 2);
    const packetScore = Math.max(0, 100 - loss * 1);
  
    // 4. Final clamped score
    return Math.min(100, Math.round(
      (downloadScore + uploadScore + pingScore + jitterScore + packetScore) / 5
    ));
  };
  
  // Function to simulate incremental progress
  const simulateProgress = async () => {
    const phases = [
      { progress: 5, message: 'Initializing test...' },
      { progress: 15, message: 'Testing ping and jitter...' },
      { progress: 45, message: 'Measuring download speed...' },
      { progress: 70, message: 'Measuring upload speed...' },
      { progress: 80, message: 'Gathering network information...' },
      { progress: 85, message: 'Testing packet integrity...' },
      { progress: 90, message: 'Measuring bufferbloat...' },
      { progress: 95, message: 'Tracing route to target...' },
      { progress: 100, message: 'Finalizing results...' }
    ];
    
    const totalDuration = 90 * 1000; // 90 seconds in milliseconds
    const phaseDuration = totalDuration / (phases.length - 1); // Time per phase based on the total duration
  
    for (let i = 0; i < phases.length; i++) {
      const phase = phases[i];
      setProgress(phase.progress);
      setCurrentTest(phase.message);
      
      // Simulate some incremental data updates while waiting for real data
      if (phase.progress === 15) {
        setSpeedData(prev => ({ 
          ...prev,
          ping_ms: Math.round(Math.random() * 15 + 5),
          jitter_ms: Math.round((Math.random() * 5 + 1) * 10) / 10
        }));
      } else if (phase.progress === 45) {
        setSpeedData(prev => ({ 
          ...prev,
          download_speed_mbps: Math.round((Math.random() * 30 + 5) * 10) / 10
        }));
      } else if (phase.progress === 70) {
        setSpeedData(prev => ({ 
          ...prev,
          upload_speed_mbps: Math.round((Math.random() * 15 + 2) * 10) / 10
        }));
      }
      
      // Wait based on the calculated duration for each phase
      await new Promise(resolve => setTimeout(resolve, phaseDuration));
    }
  };
  
  // Run test function
  const runTest = async () => {
    setTestStatus('running');
    setProgress(0);
    setLoading(true);
    setError(null);
    
    // Reset metrics to initial values
    setSpeedData({
      download_speed_mbps: 0,
      upload_speed_mbps: 0,
      ping_ms: 0,
      jitter_ms: 0
    });
    
    setIpInfo({
      public_ip: '0.0.0.0',
      local_ip: '0.0.0.0',
      isp: 'Unknown',
      asn: 'AS0000',
      dns_servers: []
    });
    
    setPacketLoss(0);
    setNatType('Unknown');
    setBufferbloat({
      idle_latency_ms: 0,
      download_latency_ms: 0,
      upload_latency_ms: 0,
      bufferbloat_ms: 0
    });
    
    setTraceroute({ hops: [] });
    
    try {
      // Start the progress simulation
      const progressSimulation = simulateProgress();
      
      // Make the actual API call
      const response = await fetch('/v1/network_test');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      
      // Wait for the progress simulation to complete
      await progressSimulation;
      
      // Update with real data
      setSpeedData(data.speedtest);
      setIpInfo(data.ip_info);
      setPacketLoss(data.packet_loss_percent);
      setNatType(data.nat_type);
      setBufferbloat(data.bufferbloat);
      
      // Calculate connection quality
      const qualityScore = calculateConnectionQuality(data.speedtest, data.packet_loss_percent);
      setConnectionQuality(qualityScore);
      
      // Add to history
      const timestamp = new Date().toLocaleString();
      setTestHistory(prev => [
        { 
          timestamp, 
          download: data.speedtest.download_speed_mbps, 
          upload: data.speedtest.upload_speed_mbps, 
          ping: data.speedtest.ping_ms 
        },
        ...prev.slice(0, 9) // Keep last 10 records
      ]);
      
      setTestStatus('completed');
      setCurrentTest('');
    } catch (err) {
      setError(err.message);
      setTestStatus('completed');
      setCurrentTest('Test failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to get color based on metric quality
  const getQualityColor = (value, type) => {
    let percentage;
    
    switch (type) {
      case 'download':
        percentage = Math.min(100, value / 2); // 200+ Mbps is excellent
        break;
      case 'upload':
        percentage = Math.min(100, value * 2); // 50+ Mbps is excellent
        break;
      case 'ping':
        percentage = Math.max(0, 100 - value * 2); // 0-20ms is excellent
        break;
      case 'jitter':
        percentage = Math.max(0, 100 - value * 10); // 0-5ms is excellent
        break;
      case 'packetLoss':
        percentage = Math.max(0, 100 - value * 20); // 0% is excellent
        break;
      case 'bufferbloat':
        percentage = Math.max(0, 100 - value * 4); // 0-10ms is excellent
        break;
      default:
        percentage = 50;
    }
    
    if (percentage > 80) return 'text-green-400';
    if (percentage > 60) return 'text-blue-400';
    if (percentage > 40) return 'text-yellow-400';
    if (percentage > 20) return 'text-orange-500';
    return 'text-red-500';
  };
  
  // Function to get dash array for circular progress
  const getCircleDashArray = (percentage) => {
    const circumference = 2 * Math.PI * 38;
    return `${circumference * percentage / 100} ${circumference * (1 - percentage / 100)}`;
  };
  
  // Function to render quality indicator
  const renderQualityIndicator = (value, max, type) => {
    const percentage = ((type === 'ping' || type === 'jitter' || type === 'packetLoss' || type === 'bufferbloat') 
      ? Math.max(0, 100 - (value / max) * 100)
      : Math.min(100, (value / max) * 100));
    
    return (
      <div className="w-full h-1 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${
            percentage > 80 ? 'bg-green-500' : 
            percentage > 60 ? 'bg-blue-500' : 
            percentage > 40 ? 'bg-yellow-500' : 
            percentage > 20 ? 'bg-orange-500' : 
            'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 p-4">
      <div className="w-full max-w-6xl bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 px-8 py-6 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white">Network Diagnostics</h1>
              <p className="text-gray-400 text-sm">Comprehensive network performance analysis</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-xs text-gray-400">Server</div>
                <select 
                  className="bg-gray-700 text-white text-sm rounded-lg px-3 py-1 border border-gray-600"
                  value={selectedServer}
                  onChange={(e) => setSelectedServer(parseInt(e.target.value))}
                  disabled={testStatus === 'running'}
                >
                  {serverList.map(server => (
                    <option key={server.id} value={server.id}>
                      {server.name} ({server.latency})
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={runTest}
                disabled={testStatus === 'running'}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-all 
                  ${testStatus === 'running' 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800'
                  }`}
              >
                {testStatus === 'running' ? 'Test Running...' : 'Start Test'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
              Error: {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Scanner and Main Metrics */}
            <div className="lg:col-span-1">
              {/* Network Scanner Visualization */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 shadow-lg mb-8">
                <div className="relative w-full aspect-square flex items-center justify-center">
                  {/* Outer ring */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                    {/* Background circles */}
                    <circle cx="50" cy="50" r="46" fill="none" stroke="#1e293b" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#1e293b" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="30" fill="none" stroke="#1e293b" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="22" fill="none" stroke="#1e293b" strokeWidth="0.5" />
                    
                    {/* Grid lines */}
                    <line x1="4" y1="50" x2="96" y2="50" stroke="#1e293b" strokeWidth="0.2" />
                    <line x1="50" y1="4" x2="50" y2="96" stroke="#1e293b" strokeWidth="0.2" />
                    <line x1="15" y1="15" x2="85" y2="85" stroke="#1e293b" strokeWidth="0.2" />
                    <line x1="15" y1="85" x2="85" y2="15" stroke="#1e293b" strokeWidth="0.2" />
                    
                    {/* Progress circle */}
                    {testStatus !== 'idle' && (
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="38" 
                        fill="none" 
                        stroke="url(#progressGradient)" 
                        strokeWidth="2"
                        strokeDasharray={getCircleDashArray(progress)}
                        strokeDashoffset="0"
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                    )}
                    
                    {/* Scan line */}
                    {testStatus === 'running' && (
                      <line 
                        x1="50" 
                        y1="50" 
                        x2={50 + 42 * Math.cos(scanAngle * Math.PI / 180)} 
                        y2={50 + 42 * Math.sin(scanAngle * Math.PI / 180)} 
                        stroke="#60a5fa" 
                        strokeWidth="0.5" 
                      />
                    )}
                    
                    {/* Scan arc */}
                    {testStatus === 'running' && (
                      <path 
                        d={`M 50 50 L ${50 + 42 * Math.cos((scanAngle - 30) * Math.PI / 180)} ${50 + 42 * Math.sin((scanAngle - 30) * Math.PI / 180)} A 42 42 0 0 1 ${50 + 42 * Math.cos(scanAngle * Math.PI / 180)} ${50 + 42 * Math.sin(scanAngle * Math.PI / 180)} Z`} 
                        fill="rgba(96, 165, 250, 0.1)" 
                        stroke="none" 
                      />
                    )}
                    
                    {/* Gradients */}
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#6366f1" />
                      </linearGradient>
                    </defs>
                  </svg>
                  
                  {/* Center content */}
                  {/* Center content */}
<div className="relative z-10 text-center">
  {testStatus === 'running' ? (
    <>
      <div className="text-2xl font-bold text-blue-400">{progress}%</div>
      <div className="text-xs text-gray-400 mt-1">{currentTest}</div>
    </>
  ) : testStatus === 'completed' ? (
    <>
      <div className="text-xl font-bold text-white">Quality Score</div>
      {typeof connectionQuality === 'string' ? ( // Handle "Unknown" case
        <div className="text-3xl font-bold mt-1 text-gray-400">
          {connectionQuality}
        </div>
      ) : (
        <>
          <div className={`text-3xl font-bold mt-1 ${
            connectionQuality > 80 ? 'text-green-400' : 
            connectionQuality > 60 ? 'text-blue-400' : 
            connectionQuality > 40 ? 'text-yellow-400' : 
            connectionQuality > 20 ? 'text-orange-500' : 
            'text-red-500'
          }`}>
            {connectionQuality}/100
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {connectionQuality > 80 ? 'Excellent' : 
             connectionQuality > 60 ? 'Good' : 
             connectionQuality > 40 ? 'Average' : 
             connectionQuality > 20 ? 'Poor' : 
             'Very Poor'}
          </div>
        </>
      )}
    </>
  ) : (
    <div className="text-gray-500 text-sm">Start test to analyze<br/>network performance</div>
  )}
</div>
                </div>
              </div>
              
              {/* Primary Metrics */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 shadow-lg">
                <h2 className="text-sm font-medium text-gray-400 mb-4">PRIMARY METRICS</h2>
                
                <div className="space-y-4">
                  {/* Download Speed */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-xs text-gray-500">Download</div>
                      <div className={`text-lg font-bold ${getQualityColor(speedData.download_speed_mbps, 'download')}`}>
                        {speedData.download_speed_mbps.toFixed(1)} <span className="text-xs font-normal">Mbps</span>
                      </div>
                    </div>
                    {renderQualityIndicator(speedData.download_speed_mbps, 100, 'download')}
                  </div>
                  
                  {/* Upload Speed */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-xs text-gray-500">Upload</div>
                      <div className={`text-lg font-bold ${getQualityColor(speedData.upload_speed_mbps, 'upload')}`}>
                        {speedData.upload_speed_mbps.toFixed(1)} <span className="text-xs font-normal">Mbps</span>
                      </div>
                    </div>
                    {renderQualityIndicator(speedData.upload_speed_mbps, 50, 'upload')}
                  </div>
                  
                  {/* Ping */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-xs text-gray-500">Ping</div>
                      <div className={`text-lg font-bold ${getQualityColor(speedData.ping_ms, 'ping')}`}>
                        {speedData.ping_ms} <span className="text-xs font-normal">ms</span>
                      </div>
                    </div>
                    {renderQualityIndicator(speedData.ping_ms, 50, 'ping')}
                  </div>
                  
                  {/* Jitter */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-xs text-gray-500">Jitter</div>
                      <div className={`text-lg font-bold ${getQualityColor(speedData.jitter_ms, 'jitter')}`}>
                        {speedData?.jitter_ms || 0} <span className="text-xs font-normal">ms</span>
                      </div>
                    </div>
                    {renderQualityIndicator(speedData?.jitter_ms || 0, 10, 'jitter')}
                  </div>
                  
                  {/* Packet Loss */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-xs text-gray-500">Packet Loss</div>
                      <div className={`text-lg font-bold ${getQualityColor(packetLoss, 'packetLoss')}`}>
                        {packetLoss.toFixed(1)} <span className="text-xs font-normal">%</span>
                      </div>
                    </div>
                    {renderQualityIndicator(packetLoss, 5, 'packetLoss')}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Detailed Technical Information */}
            <div className="lg:col-span-2 space-y-8">
              {/* Network Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* IP Information */}
                <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 shadow-lg">
                  <h2 className="text-sm font-medium text-gray-400 mb-4">IP INFORMATION</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">Public IP</div>
                      <div className="col-span-3 text-sm font-medium text-blue-400 font-mono">{ipInfo.public_ip}</div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">Local IP</div>
                      <div className="col-span-3 text-sm font-medium text-white font-mono">{ipInfo.local_ip}</div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">ISP</div>
                      <div className="col-span-3 text-sm font-medium text-white">{ipInfo.isp}</div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">ASN</div>
                      <div className="col-span-3 text-sm font-medium text-white">{ipInfo.asn}</div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">NAT Type</div>
                      <div className="col-span-3 text-sm font-medium text-white">{natType}</div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-2 text-xs text-gray-500">DNS Servers</div>
                      <div className="col-span-3 text-sm font-medium text-gray-300 font-mono">
                        {ipInfo.dns_servers.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bufferbloat Analysis */}
                <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 shadow-lg">
                  <h2 className="text-sm font-medium text-gray-400 mb-4">BUFFERBLOAT ANALYSIS</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-3 text-xs text-gray-500">Idle Latency</div>
                      <div className="col-span-2 text-sm font-medium text-white text-right">
                        {bufferbloat.idle_latency_ms} ms
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-3 text-xs text-gray-500">Download Latency</div>
                      <div className="col-span-2 text-sm font-medium text-white text-right">
                        {bufferbloat.download_latency_ms} ms
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-3 text-xs text-gray-500">Upload Latency</div>
                      <div className="col-span-2 text-sm font-medium text-white text-right">
                        {bufferbloat.upload_latency_ms} ms
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-5 items-center gap-2">
                      <div className="col-span-3 text-xs text-gray-500">Bufferbloat</div>
                      <div className={`col-span-2 text-sm font-medium text-right ${getQualityColor(bufferbloat.bufferbloat_ms, 'bufferbloat')}`}>
                        {bufferbloat.bufferbloat_ms} ms
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <div className="text-xs text-gray-500 mb-1">Bufferbloat Impact</div>
                      {renderQualityIndicator(bufferbloat.bufferbloat_ms, 25, 'bufferbloat')}
                      <div className="text-xs text-gray-500 mt-2">
                        {bufferbloat.bufferbloat_ms < 10 
                          ? 'Excellent: No impact on performance' 
                          : bufferbloat.bufferbloat_ms < 20 
                          ? 'Good: Minimal impact on performance' 
                          : bufferbloat.bufferbloat_ms < 30 
                          ? 'Average: Some impact during heavy usage' 
                          : 'Poor: Significant impact, consider QoS adjustments'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Test History */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 shadow-lg">
                <h2 className="text-sm font-medium text-gray-400 mb-4">TEST HISTORY</h2>
                
                {testHistory.length > 0 ? (
                  <div className="overflow-hidden">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="pb-2">Date</th>
                          <th className="pb-2 text-right">Download (Mbps)</th>
                          <th className="pb-2 text-right">Upload (Mbps)</th>
                          <th className="pb-2 text-right">Ping (ms)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testHistory.map((test, index) => (
                          <tr key={index} className="even:bg-gray-800">
                            <td className="py-2 text-gray-300">{test.timestamp}</td>
                            <td className="py-2 text-white text-right">{test?.download || 0} Mbps</td>
                            <td className="py-2 text-white text-right">{test?.upload || 0} Mbps</td>
                            <td className="py-2 text-white text-right">{test.ping} ms</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500">No test history available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedTest;
